import { EImportSectionsTarget } from '@constants';
import { Collapse, Flex, Input } from 'antd';
import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { TitleWrapper, ItemDescriptionWrapper, Wrapper } from './Introduction.styles';

import { ActivityPreview } from '@/components/ActivityPreview';
import { ContentSearchEmptyPage } from '@/components/ContentSearchEmptyPage';
import { Icon } from '@/components/Icon';
import { IntroductionActivities } from '@/components/IntrouctionSection/IntroductionActivities';
import { IntroductionForm } from '@/components/IntrouctionSection/IntroductionForm';
import {
  AddSection,
  EmptySections,
  ImportSection,
  SectionActions
} from '@/components/SectionManagement';
import { SectionName } from '@/components/SectionName';
import { Box } from '@/components/Utils';
import { useSearchSections } from '@/hooks';
import { useLocalCourse } from '@/store';
import { ECourseContentStep } from '@/types';

export const Introduction: React.FC = () => {
  const [openSectionIds, setOpenSectionIds] = useState<string[]>([]);
  const { t } = useTranslation('ManagerBaseContentDetailTranslations');
  const { course } = useLocalCourse();
  const { colors } = useTheme();
  const target = EImportSectionsTarget.INTRODUCTION;
  const [searchParams] = useSearchParams();
  const sectionId = searchParams.get('sectionId');
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  useEffect(() => {
    if (!sectionId) return;

    setOpenSectionIds(prev => (prev.includes(sectionId) ? prev : [...prev, sectionId]));

    const timeoutId = setTimeout(() => {
      const sectionElement = sectionRefs.current[sectionId];
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [sectionId]);

  const {
    searchValue,
    setSearchValue,
    filteredSections: filteredIntroductions
  } = useSearchSections(course?.introduction);

  if (!course) return null;

  const { introduction = [] } = course;
  const isSectionsEmpty = isEmpty(introduction);
  const isSectionsFilteredEmpty = isEmpty(filteredIntroductions) && !isSectionsEmpty;

  return (
    <Wrapper>
      <TitleWrapper>{t(ECourseContentStep.INTRODUCTION)}</TitleWrapper>
      <Flex justify="space-between" align="center">
        {!isSectionsEmpty && (
          <Input.Search
            placeholder={t('SEARCH_SECTIONS_OR_ACTIVITIES_BY_NAME')}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            allowClear
            style={{ width: '100%', maxWidth: 400 }}
          />
        )}
        <Flex gap={8}>
          {!isSectionsEmpty && (
            <>
              <AddSection course={course} target={target} />
              <ImportSection target={target} />
            </>
          )}
        </Flex>
      </Flex>
      {isSectionsEmpty && <EmptySections course={course} target={target} />}
      {isSectionsFilteredEmpty && <ContentSearchEmptyPage />}
      <Flex vertical>
        {filteredIntroductions.map(section => (
          <div
            key={section._id}
            ref={el => {
              sectionRefs.current[section._id] = el;
            }}>
            <Collapse
              {...section}
              activeKey={openSectionIds.includes(section._id) ? [section._id] : []}
              style={{
                marginBottom: 16,
                borderRadius: 4,
                borderColor: 'transparent',
                backgroundColor: section.active ? colors.black1 : colors.black2
              }}
              onChange={() => {
                setOpenSectionIds(prev =>
                  prev.includes(section._id)
                    ? prev.filter(id => id !== section._id)
                    : [...prev, section._id]
                );
              }}
              expandIcon={({ isActive }) => (
                <Box gap={10}>
                  <Icon i={isActive ? 'ChevronDown' : 'ChevronRight'} size={16} />
                </Box>
              )}
              items={[
                {
                  key: section._id,
                  label: (
                    <Flex vertical gap={16}>
                      <SectionName section={section} />
                      <ItemDescriptionWrapper>{section.description}</ItemDescriptionWrapper>
                    </Flex>
                  ),
                  extra: <SectionActions target={target} courseId={course._id} section={section} />,
                  children: <IntroductionActivities section={section} />
                }
              ]}
            />
          </div>
        ))}
      </Flex>
      <ActivityPreview />
      <IntroductionForm />
    </Wrapper>
  );
};
